import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Construction, Lightbulb, Trash2, Droplets, Shield, Waves, TrafficCone, LocateFixed, Camera, Loader2, CheckCircle2, ArrowLeft, ArrowRight, X } from "lucide-react";
import { api, errMsg, compressImage } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, CopyText } from "@/components/common";
import { GeoMap } from "@/components/GeoMap";
import { useUserLocation, placeLabel } from "@/lib/location";
import { Switch } from "@/components/ui/switch";
import { DynamicFields, missingRequired } from "@/components/community";

const ICONS = { bache: Construction, alumbrado: Lightbulb, basura: Trash2, agua: Droplets, seguridad: Shield, sargazo: Waves, semaforo: TrafficCone };
const STEPS = ["Categoría", "Ubicación", "Detalles", "Confirmar"];

export default function NewReport() {
  const navigate = useNavigate();
  const cats = useFetch("/meta/catalogs");
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState(null);
  const [pos, setPos] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [locating] = useState(false);
  const [address, setAddress] = useState("");
  const [desc, setDesc] = useState("");
  const [photo, setPhoto] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [extra, setExtra] = useState({});
  const [sending, setSending] = useState(false);
  const [created, setCreated] = useState(null);
  const [fly, setFly] = useState(null);
  const loc = useUserLocation();
  const [manualPin, setManualPin] = useState(false);

  // Usa la ubicación exacta del GPS automáticamente (mientras el usuario no mueva el pin a mano)
  useEffect(() => {
    if (loc.position && !manualPin) {
      setPos(loc.position); setAccuracy(loc.coords.accuracy);
      if (step === 1) setFly(loc.position);
    }
  }, [loc.coords?.lat, loc.coords?.lng, manualPin, step]); // eslint-disable-line
  useEffect(() => {
    if (!manualPin && loc.place && !address) setAddress(placeLabel(loc.place) || "");
  }, [loc.place, manualPin]); // eslint-disable-line

  const locate = () => {
    setManualPin(false);
    if (loc.position) { setPos(loc.position); setFly([...loc.position]); setAccuracy(loc.coords.accuracy); setAddress(placeLabel(loc.place) || address); }
    loc.request();
    if (loc.status === "denied") toast.error("Ubicación bloqueada: actívala desde el candado del navegador o toca el mapa.");
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Selecciona una imagen");
    try { setPhoto(await compressImage(f)); setThumb(await compressImage(f, 360, 0.6)); } catch { toast.error("No se pudo procesar la foto"); }
  };

  const formFields = (category && cats.data?.category_forms?.[category.id]) || [];
  const submit = async () => {
    setSending(true);
    try {
      const colonia = !manualPin ? (loc.place?.suburb || null) : null;
      const { data } = await api.post("/reports", { category: category.id, description: desc, lat: pos[0], lng: pos[1], accuracy_m: accuracy, address, photo, photo_thumb: thumb, is_public: isPublic, extra, colonia });
      setCreated(data);
      toast.success(`Folio ${data.folio} generado`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  if (created) {
    return (
      <div className="max-w-xl mx-auto fade-up" data-testid="report-success">
        <Card><CardContent className="p-8 text-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
          <h2 className="text-2xl font-semibold">¡Reporte registrado!</h2>
          <p className="text-muted-foreground">Turnado a la dependencia responsable. Te avisaremos por WhatsApp cada cambio.</p>
          <div className="rounded-lg bg-secondary p-4">
            <div className="text-xs text-muted-foreground">Tu folio único</div>
            <CopyText value={created.folio} className="text-lg font-semibold justify-center" testid="report-folio-copy-button" />
          </div>
          {created.possible_duplicate_of && (
            <Alert className="text-left bg-amber-50 border-amber-200"><AlertDescription>Detectamos un reporte similar cercano ({created.possible_duplicate_of}); lo vincularemos para atenderlo más rápido.</AlertDescription></Alert>
          )}
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button onClick={() => navigate(`/app/reportes/${created.folio}`)} data-testid="report-success-track-button">Ver seguimiento</Button>
            <Button variant="outline" onClick={() => { setCreated(null); setStep(0); setCategory(null); setPos(null); setDesc(""); setPhoto(null); setThumb(null); setExtra({}); setAddress(""); }}>Nuevo reporte</Button>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  const canNext = [!!category, !!pos, desc.trim().length >= 10 && missingRequired(formFields, extra).length === 0, true][step];

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader eyebrow="Comunidad" title="Nuevo reporte ciudadano" subtitle="4 pasos · menos de un minuto" />
      <div className="flex items-center gap-2 mb-6" data-testid="report-stepper">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full ${i <= step ? "bg-primary" : "bg-border"}`} />
            <div className={`text-xs mt-1.5 ${i === step ? "text-foreground font-medium" : "text-muted-foreground"}`}>{i + 1}. {s}</div>
          </div>
        ))}
      </div>

      <Card><CardContent className="p-5 md:p-6">
        {step === 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(cats.data?.categories || []).map((c) => {
              const I = ICONS[c.id] || Construction;
              const active = category?.id === c.id;
              return (
                <button key={c.id} onClick={() => setCategory(c)} data-testid={`report-category-${c.id}`}
                  className={`rounded-xl border p-4 text-left transition-colors ${active ? "border-primary bg-[#DFF3F6]" : "hover:bg-secondary"}`}>
                  <I className={`h-6 w-6 ${active ? "text-primary" : "text-muted-foreground"}`} />
                  <div className="mt-2 text-sm font-medium">{c.label}</div>
                  <div className="text-[11px] text-muted-foreground">{cats.data.dependencies[c.dependency]}</div>
                </button>
              );
            })}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">{loc.position ? "Detectamos tu ubicación exacta por GPS. Puedes ajustar el pin tocando el mapa." : "Permite la ubicación del navegador o toca el mapa para colocar el pin."}</p>
              <Button variant="outline" onClick={locate} disabled={loc.status === "locating"} data-testid="report-locate-button">
                {loc.status === "locating" || locating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <LocateFixed className="h-4 w-4 mr-1" />} Usar mi ubicación exacta
              </Button>
            </div>
            <GeoMap className="h-[380px]" testid="report-map-container" center={loc.position || [21.1619, -86.8515]} zoom={loc.position ? 16 : 12} pitch={0} bearing={0} compact
              onPick={(c) => { setManualPin(true); setPos(c); setAccuracy(null); }} picked={pos} flyTo={fly} flyZoom={17} />
            <div className="text-xs font-mono text-muted-foreground" data-testid="report-coordinates">
              {pos ? `${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}${accuracy ? ` · GPS ±${accuracy} m` : " · pin manual"}` : "Sin ubicación seleccionada"}
            </div>
            {!manualPin && loc.place && (
              <div className="text-xs rounded-md bg-[#DFF3F6] text-[#006A7C] px-3 py-2" data-testid="report-detected-address">
                Dirección detectada: {placeLabel(loc.place)}{loc.place.municipio ? ` · ${loc.place.municipio}` : ""}{!loc.place.in_qroo ? " (fuera de Quintana Roo: coloca el pin dentro del estado)" : ""}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="addr">Referencia (opcional)</Label>
              <Input id="addr" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Av. Tulum esq. Cobá, frente a la farmacia" data-testid="report-address-input" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="desc">Describe el problema</Label>
              <Textarea id="desc" rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Ej. Bache de 1 m frente a la escuela, riesgo para motociclistas." data-testid="report-description-input" />
              <div className="text-xs text-muted-foreground">{desc.trim().length}/10 caracteres mínimos</div>
            </div>
            {formFields.length > 0 && (
              <div className="rounded-lg border p-3 bg-secondary/30">
                <div className="text-xs font-medium uppercase tracking-[0.12em] text-primary mb-2">Datos que pide {cats.data.dependencies[category.dependency]}</div>
                <DynamicFields fields={formFields} values={extra} onChange={setExtra} prefix="report-extra" />
              </div>
            )}
            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div>
                <Label htmlFor="pub" className="font-medium">Hacer público</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Tus vecinos podrán verlo en el mapa, votarlo, seguirlo y sumar comentarios o fotos. Solo se muestra tu primer nombre.</p>
              </div>
              <Switch id="pub" checked={isPublic} onCheckedChange={setIsPublic} data-testid="report-public-switch" />
            </div>
            <div className="space-y-1.5">
              <Label>Foto de evidencia (opcional)</Label>
              {photo ? (
                <div className="relative w-fit">
                  <img src={photo} alt="evidencia" className="h-40 rounded-lg border object-cover" data-testid="report-photo-preview" />
                  <button className="absolute top-1 right-1 rounded-full bg-white p-1 shadow" onClick={() => { setPhoto(null); setThumb(null); }} aria-label="Quitar"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <label className="flex items-center gap-3 rounded-lg border-2 border-dashed p-4 cursor-pointer hover:bg-secondary transition-colors">
                  <Camera className="h-5 w-5 text-primary" /><span className="text-sm">Tomar o subir foto (se comprime automáticamente)</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="report-photo-input" />
                </label>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3 text-sm" data-testid="report-summary">
            <div className="rounded-lg bg-secondary p-4 space-y-1.5">
              <div><span className="text-muted-foreground">Categoría:</span> {category.label}</div>
              <div><span className="text-muted-foreground">Dependencia:</span> {cats.data.dependencies[category.dependency]}</div>
              <div className="font-mono text-xs"><span className="text-muted-foreground font-sans text-sm">Ubicación:</span> {pos[0].toFixed(5)}, {pos[1].toFixed(5)}</div>
              <div><span className="text-muted-foreground">Descripción:</span> {desc}</div>
              <div><span className="text-muted-foreground">Foto:</span> {photo ? "Sí" : "No"}</div>
              <div><span className="text-muted-foreground">Visibilidad:</span> {isPublic ? "Pública (vecinos pueden votar y comentar)" : "Privada"}</div>
              {formFields.filter((f) => extra[f.key] !== undefined && extra[f.key] !== "").map((f) => <div key={f.key}><span className="text-muted-foreground">{f.label}:</span> {f.type === "checkbox" ? (extra[f.key] ? "Sí" : "No") : String(extra[f.key])}</div>)}
            </div>
            <p className="text-xs text-muted-foreground">Al enviar se genera un folio único, se calcula su huella SHA-256 y se ancla en la bitácora de auditoría.</p>
          </div>
        )}

        <div className="flex justify-between mt-6">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="report-back-button"><ArrowLeft className="h-4 w-4 mr-1" />Atrás</Button>
          {step < 3 ? (
            <Button disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="report-next-button">Continuar <ArrowRight className="h-4 w-4 ml-1" /></Button>
          ) : (
            <Button onClick={submit} disabled={sending} className="btn-press" data-testid="report-submit-button">
              {sending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Enviar reporte
            </Button>
          )}
        </div>
      </CardContent></Card>
    </div>
  );
}
